<!--
  Nuovo cliente.

  · il TIPO (privato / azienda) si sceglie con due card; il link con ?q=1
    imposta solo il tipo iniziale;
  · i campi fiscali (P.IVA, CF, SDI, PEC) compaiono solo per le aziende;
  · l'indirizzo viene geolocalizzato e l'esito è visibile;
  · le auto sono card ripetibili e il pacchetto valet si compila dentro
    l'auto a cui appartiene — a database `minute` è legata a `car`, non al
    cliente.
-->
<template>
  <div class="acs">
    <!-- ---------- intestazione ---------- -->
    <div class="acs__head">
      <el-button
        class="acs__back"
        text
        @click="$router.push('/customer')"
      >
        ‹
      </el-button>
      <div>
        <h1 class="acs__title">
          Nuovo cliente
        </h1>
        <p class="acs__sub">
          Anagrafica, indirizzo e auto. I pacchetti valet si aggiungono sulla singola auto.
        </p>
      </div>
    </div>

    <!-- ---------- 1. tipo ---------- -->
    <section class="acs__card">
      <header class="acs__cardHead">
        <span class="acs__step">1</span>
        <h3>Tipo di cliente</h3>
      </header>
      <div class="acs__cardBody">
        <div class="acs__types">
          <button
            v-for="t in types"
            :key="t.key"
            type="button"
            class="acs__type"
            :class="{ 'is-active': isCompany === t.company }"
            @click="isCompany = t.company"
          >
            <span class="acs__typeIcon">{{ t.icon }}</span>
            <span>
              <span class="acs__typeName">{{ t.name }}</span>
              <span class="acs__typeHint">{{ t.hint }}</span>
            </span>
          </button>
        </div>
      </div>
    </section>

    <!-- ---------- 2. anagrafica ---------- -->
    <section class="acs__card">
      <header class="acs__cardHead">
        <span class="acs__step">2</span>
        <h3>{{ isCompany ? 'Dati azienda' : 'Dati anagrafici' }}</h3>
      </header>
      <div class="acs__cardBody">
        <div class="acs__grid">
          <template v-if="isCompany">
            <div class="acs__field acs__field--full">
              <label>Ragione sociale <span class="acs__req">*</span></label>
              <el-input
                v-model="customer.company_name"
                placeholder="es. Autotorino S.p.A."
              />
            </div>
            <div class="acs__field">
              <label>Partita IVA</label>
              <el-input v-model="customer.piva" />
            </div>
            <div class="acs__field">
              <label>Codice fiscale</label>
              <el-input v-model="customer.CF" />
            </div>
            <div class="acs__field">
              <label>Codice SDI</label>
              <el-input v-model="customer.SDI" />
            </div>
            <div class="acs__field">
              <label>PEC</label>
              <el-input v-model="customer.PEC" />
            </div>
          </template>

          <div class="acs__field">
            <label>Nome <span class="acs__req">*</span></label>
            <el-input
              v-model="customer.name"
              :placeholder="isCompany ? 'Nome del referente' : ''"
            />
          </div>
          <div class="acs__field">
            <label>Cognome <span class="acs__req">*</span></label>
            <el-input
              v-model="customer.surname"
              :placeholder="isCompany ? 'Cognome del referente' : ''"
            />
          </div>
          <div class="acs__field">
            <label>Email <span class="acs__req">*</span></label>
            <el-input
              v-model="customer.mail"
              placeholder="serve per l'accesso all'app"
            />
          </div>
          <div class="acs__field">
            <label>Telefono <span class="acs__req">*</span></label>
            <el-input v-model="customer.telephone_number" />
          </div>
          <div
            v-if="!isCompany"
            class="acs__field"
          >
            <label>Data di nascita</label>
            <el-date-picker
              v-model="customer.date_birth"
              type="date"
              format="DD/MM/YYYY"
              placeholder="Seleziona"
              style="width: 100%"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- ---------- 3. indirizzo ---------- -->
    <section class="acs__card">
      <header class="acs__cardHead">
        <span class="acs__step">3</span>
        <h3>Indirizzo</h3>
      </header>
      <div class="acs__cardBody">
        <div class="acs__grid">
          <div class="acs__field acs__field--wide">
            <label>Via / Piazza <span class="acs__req">*</span></label>
            <el-input
              v-model="customer.address"
              @blur="geocode"
            />
          </div>
          <div class="acs__field acs__field--narrow">
            <label>N. civico <span class="acs__req">*</span></label>
            <el-input
              v-model="customer.street_number"
              @blur="geocode"
            />
          </div>
          <div class="acs__field acs__field--narrow">
            <label>CAP <span class="acs__req">*</span></label>
            <el-input
              v-model="customer.cap"
              @blur="geocode"
            />
          </div>
          <div class="acs__field">
            <label>Città <span class="acs__req">*</span></label>
            <el-input
              v-model="customer.city"
              @blur="geocode"
            />
          </div>
          <div class="acs__field acs__field--narrow">
            <label>Provincia <span class="acs__req">*</span></label>
            <el-input
              v-model="customer.province"
              placeholder="MI"
              @blur="geocode"
            />
          </div>
        </div>

        <!-- esito della geolocalizzazione -->
        <div
          v-if="geoState === 'loading'"
          class="acs__geo acs__geo--wait"
        >
          Verifica dell'indirizzo in corso…
        </div>
        <div
          v-else-if="geoState === 'ok'"
          class="acs__geo acs__geo--ok"
        >
          📍 Indirizzo confermato: <b>{{ geoLabel }}</b>
          <span
            v-if="geoAlternatives > 0"
            class="acs__geoAlt"
          >
            (il geocoder ha trovato altri {{ geoAlternatives }} risultati simili: verifica che sia quello giusto)
          </span>
        </div>
        <div
          v-else-if="geoState === 'fail'"
          class="acs__geo acs__geo--fail"
        >
          Indirizzo non trovato. Controlla i campi: senza coordinate il cliente
          viene comunque creato, ma il calcolo delle distanze non funzionerà.
        </div>
      </div>
    </section>

    <!-- ---------- 4. auto ---------- -->
    <section class="acs__card">
      <header class="acs__cardHead">
        <span class="acs__step">4</span>
        <h3>Auto</h3>
        <span class="acs__count">{{ cars.length }}</span>
        <el-button
          size="small"
          @click="addCarRow"
        >
          Aggiungi auto
        </el-button>
      </header>
      <div class="acs__cardBody">
        <p class="acs__note">
          L'auto non è obbligatoria: si può aggiungere anche in seguito dalla scheda del cliente.
        </p>

        <article
          v-for="(car, i) in cars"
          :key="i"
          class="acs__car"
        >
          <div class="acs__carHead">
            <span class="acs__carTitle">Auto {{ i + 1 }}</span>
            <div class="acs__spacer" />
            <el-button
              text
              size="small"
              class="acs__remove"
              @click="removeCarRow(i)"
            >
              Rimuovi
            </el-button>
          </div>

          <div class="acs__grid">
            <div class="acs__field">
              <label>Marca <span class="acs__req">*</span></label>
              <el-input
                v-model="car.marca"
                placeholder="es. BMW"
              />
            </div>
            <div class="acs__field">
              <label>Modello <span class="acs__req">*</span></label>
              <el-input
                v-model="car.modello"
                placeholder="es. Serie 3"
              />
            </div>
            <div class="acs__field">
              <label>Targa <span class="acs__req">*</span></label>
              <el-input
                v-model="car.targa"
                placeholder="es. FA123ZZ"
              />
            </div>
            <div class="acs__field">
              <label>Intestatario</label>
              <el-input
                v-model="car.intestatario"
                placeholder="Intestatario del veicolo"
                @input="car.intestatarioTouched = true"
              />
            </div>
            <div class="acs__field">
              <label>Cambio</label>
              <el-select
                v-model="car.transmission"
                style="width: 100%"
              >
                <el-option
                  label="Automatico"
                  value="automatic"
                />
                <el-option
                  label="Manuale"
                  value="manual"
                />
              </el-select>
            </div>
          </div>

          <!-- valet: appartiene a QUESTA auto -->
          <div class="acs__valet">
            <div class="acs__valetHead">
              <span class="acs__valetTitle">Pacchetto valet</span>
              <el-switch v-model="car.hasValet" />
            </div>
            <!-- Prezzo dal listino valet del partner: il pacchetto nasce con
                 partner ed email di conferma, che il consumo richiede. -->
            <div
              v-if="car.hasValet"
              class="acs__valetBody"
            >
              <div class="acs__field">
                <label :for="`acs-valet-partner-${i}`">Partner del pacchetto <span class="acs__req">*</span></label>
                <el-select
                  :id="`acs-valet-partner-${i}`"
                  v-model="car.partnerId"
                  filterable
                  placeholder="Scegli il partner"
                  style="width: 100%"
                >
                  <el-option
                    v-for="p in partners"
                    :key="p.id"
                    :label="p.company_name"
                    :value="p.id"
                  />
                </el-select>
              </div>
              <ValetProposal
                :address="customer"
                :partner-id="car.partnerId"
                @change="(e) => (car.proposal = e)"
              />
              <p class="acs__valetHint">
                Creando il cliente parte l'email per confermare il pacchetto:
                finché il cliente non conferma resta da confermare e non si può
                usare nelle prenotazioni.
              </p>
            </div>
          </div>
        </article>
      </div>
    </section>

    <!-- ---------- barra azioni ---------- -->
    <div class="acs__bar">
      <div class="acs__barInner">
        <span
          v-if="firstError"
          class="acs__error"
        >{{ firstError }}</span>
        <div class="acs__spacer" />
        <el-button @click="$router.push('/customer')">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="saving"
          @click="submit"
        >
          Crea cliente
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import customerAPI from '../../services/customerAPI.js';
import carAPI, { serverMessage } from '../../services/carAPI.js';
import prenotazioneAPI from '../../services/prenotazioneAPI.js';
import partnerAPI from '../../services/partnerAPI.js';
import { minutePayload } from '../../services/valetAPI.js';
import ValetProposal from '../../components/customer/ValetProposal.vue';

// L'intestatario nasce precompilato col nome del cliente: nella grande
// maggioranza dei casi l'auto è sua. `intestatarioTouched` ricorda se è stato
// modificato a mano, così non lo sovrascriviamo più.
const emptyCar = (owner = '') => ({
  marca: '',
  modello: '',
  targa: '',
  intestatario: owner,
  intestatarioTouched: false,
  transmission: 'automatic',
  hasValet: false,
  // Pacchetto valet a listino: partner del pacchetto ed esito di
  // `ValetProposal` ({ problem, data }).
  partnerId: '',
  proposal: null,
});

export default {
  name: 'AddCustomerPage',
  components: { ValetProposal },

  setup() {
    const route = useRoute();
    const router = useRouter();

    // Il link con ?q=1 indica "cliente privato": vale come tipo iniziale,
    // modificabile a video.
    const isCompany = ref(route.query.q != 1);

    const types = [
      { key: 'privato', company: false, icon: '👤', name: 'Privato', hint: 'Persona fisica' },
      { key: 'azienda', company: true, icon: '🏢', name: 'Azienda', hint: 'Con ragione sociale e P. IVA' },
    ];

    const customer = reactive({
      company_name: '', piva: '', CF: '', SDI: '', PEC: '',
      name: '', surname: '', mail: '', telephone_number: '',
      date_birth: null,
      address: '', street_number: '', city: '', cap: '', province: '',
      latitude: '', longitude: '', label: '',
    });

    const cars = ref([]);
    const saving = ref(false);

    // Partner fra cui scegliere quello del pacchetto valet.
    const partners = ref([]);
    onMounted(async () => {
      try {
        const { data } = await partnerAPI.getPartners();
        partners.value = Array.isArray(data) ? data : data?.data ?? [];
      } catch (e) {
        console.warn('Partner non letti:', e?.message);
      }
    });

    // ---- geolocalizzazione ------------------------------------------
    const geoState = ref('idle'); // idle | loading | ok | fail
    const geoLabel = ref('');
    const geoAlternatives = ref(0);

    const geocode = async () => {
      const { address, street_number, city, cap, province } = customer;
      // Si interroga il geocoder solo quando l'indirizzo è completo.
      if (!address || !street_number || !city || !cap || !province) {
        geoState.value = 'idle';
        return;
      }
      geoState.value = 'loading';
      try {
        // Stesso formato usato dalle altre pagine (EstimatePage): il geocoder
        // vuole "via civico cap, citta provincia".
        const text = `${address} ${street_number} ${cap}, ${city} ${province}`;
        const { data } = await prenotazioneAPI.getPoints({ text });

        // L'endpoint restituisce un ARRAY di risultati e le coordinate stanno
        // dentro `point`:  [{ point: { latitude, longitude, ... }, label }]
        const first = Array.isArray(data) ? data[0] : null;
        if (first?.point?.latitude) {
          customer.latitude = first.point.latitude;
          customer.longitude = first.point.longitude;
          customer.label = first.label || text;
          geoLabel.value = first.label || text;
          // Se il geocoder propone più indirizzi lo si segnala: l'operatore
          // deve poter capire che la scelta non è univoca.
          geoAlternatives.value = Array.isArray(data) ? Math.max(0, data.length - 1) : 0;
          geoState.value = 'ok';
        } else {
          geoState.value = 'fail';
        }
      } catch (e) {
        console.warn('Geocoding non riuscito:', e?.message);
        geoState.value = 'fail';
      }
    };

    // ---- auto --------------------------------------------------------
    // Nome da usare come intestatario di default.
    const customerFullName = computed(() =>
      (customer.company_name || `${customer.name} ${customer.surname}`).trim(),
    );

    const addCarRow = () => cars.value.push(emptyCar(customerFullName.value));
    const removeCarRow = (i) => cars.value.splice(i, 1);

    // Se l'anagrafica viene compilata DOPO aver aggiunto l'auto, l'intestatario
    // si aggiorna da solo — ma solo finché l'utente non lo ha modificato a mano
    // (`intestatarioTouched`), per non sovrascrivere una scelta esplicita.
    watch(customerFullName, (name) => {
      cars.value.forEach((c) => {
        if (!c.intestatarioTouched) c.intestatario = name;
      });
    });

    // ---- validazione -------------------------------------------------
    // Un solo messaggio alla volta, quello che blocca: più utile di dieci
    // campi rossi tutti insieme.
    const firstError = computed(() => {
      if (isCompany.value && !customer.company_name.trim()) return 'Manca la ragione sociale';
      if (!customer.name.trim()) return 'Manca il nome';
      if (!customer.surname.trim()) return 'Manca il cognome';
      if (!customer.mail.trim()) return 'Manca l\'email';
      if (!/^\S+@\S+\.\S+$/.test(customer.mail.trim())) return 'Email non valida';
      if (!customer.telephone_number.trim()) return 'Manca il telefono';
      if (!customer.address.trim() || !customer.city.trim()) return 'Manca l\'indirizzo';
      for (let i = 0; i < cars.value.length; i++) {
        const c = cars.value[i];
        if (!c.marca.trim() || !c.modello.trim() || !c.targa.trim()) {
          return `Auto ${i + 1}: marca, modello e targa sono obbligatori`;
        }
        if (c.hasValet) {
          if (!c.partnerId) return `Auto ${i + 1}: scegli il partner del pacchetto valet`;
          const problem = c.proposal ? c.proposal.problem : 'calcolo del prezzo in corso…';
          if (problem) return `Auto ${i + 1}, pacchetto valet: ${problem}`;
        }
      }
      return '';
    });

    // ---- salvataggio -------------------------------------------------
    const submit = async () => {
      if (firstError.value) {
        ElMessage({ message: firstError.value, type: 'warning' });
        return;
      }
      saving.value = true;
      try {
        // 1) cliente
        const payload = {
          ...customer,
          mail: customer.mail.trim(),
          role: 'customer',
          isAlive: true,
        };
        const res = await customerAPI.addCustomer(payload);

        // POST /auth/register risponde { stato, messaggio: <utente creato> }.
        // Gli errori arrivano ora come status HTTP (409/400) e finiscono nel
        // catch; il ramo 'reject' resta come rete di sicurezza per eventuali
        // backend non ancora aggiornati.
        if (res?.data?.stato === 'reject') {
          throw new Error(res?.data?.messaggio?.message || 'Registrazione rifiutata');
        }

        let customerId = res?.data?.messaggio?.id || null;
        // Rete di sicurezza: se l'id non arriva lo si recupera per email, che è
        // univoca.
        if (!customerId && cars.value.length) {
          const found = await customerAPI.findByMail(payload.mail);
          customerId = found?.id || null;
        }

        // Senza id non si possono agganciare le auto: il backend salverebbe
        // un veicolo ORFANO (createCar accetta customerId undefined e mette
        // customer = null), che poi non comparirebbe nella scheda cliente.
        // Meglio fermarsi e dirlo, che creare dati scollegati in silenzio.
        if (!customerId && cars.value.length) {
          ElMessage({
            message:
              'Cliente creato, ma non è stato possibile collegargli le auto: aggiungile dalla sua scheda.',
            type: 'warning',
            duration: 6000,
          });
          router.push('/customer');
          return;
        }

        // 2) auto + eventuale valet. Non bloccano la creazione del cliente:
        //    se qualcosa fallisce lo si segnala e si prosegue.
        for (const c of cars.value) {
          try {
            await carAPI.addCar({
              marca: c.marca.trim(),
              modello: c.modello.trim(),
              targa: c.targa.trim().toUpperCase(),
              intestatario: c.intestatario.trim() || customerFullName.value,
              transmission: c.transmission,
              customerId,
            });

            if (c.hasValet && c.proposal?.data) {
              const created = await carAPI.getCarByTarga(c.targa.trim().toUpperCase());
              const car = created?.data?.[0];
              if (car) {
                // Prezzo dal listino e partner del pacchetto: il consumo li richiede entrambi.
                // Try a parte: se il server rifiuta il pacchetto (400 con il
                // motivo) l'auto è comunque salvata, e va detto così.
                let minute = null;
                try {
                  ({ data: minute } = await carAPI.addMinute(minutePayload({
                    ...c.proposal.data,
                    carId: car.id,
                    partnerId: c.partnerId,
                  }), { skipErrorToast: true }));
                } catch (e) {
                  console.error('Pacchetto valet non creato:', e);
                  const detail = serverMessage(e);
                  ElMessage({
                    message: `Auto ${c.targa} salvata, ma il pacchetto valet non è stato creato${detail ? `: ${detail}` : ''}. Proponilo dalla scheda del cliente.`,
                    type: 'warning',
                    duration: 10000,
                    showClose: true,
                  });
                }
                if (minute?.id) {
                  try {
                    const r = await carAPI.mailAddMinute(minute.id);
                    if (r?.data?.stato === 'reject') throw new Error('reject');
                  } catch (e) {
                    console.error('Email del pacchetto non inviata:', e);
                    ElMessage({
                      message: `Pacchetto dell'auto ${c.targa} creato, ma l'email di conferma non è partita: lo puoi confermare dalla scheda del cliente.`,
                      type: 'warning',
                      duration: 8000,
                      showClose: true,
                    });
                  }
                }
              }
            }
          } catch (e) {
            console.error('Auto non salvata:', e);
            ElMessage({
              message: `Cliente creato, ma l'auto ${c.targa} non è stata salvata`,
              type: 'warning',
            });
          }
        }

        ElMessage({ message: 'Cliente creato', type: 'success' });
        router.push('/customer');
      } catch (e) {
        const status = e?.response?.status;
        if (status === 409) {
          ElMessage({ message: 'Email già utilizzata da un altro utente', type: 'error' });
        } else if (status === 400) {
          ElMessage({ message: 'Dati non validi: controlla i campi', type: 'error' });
        } else {
          console.error('Creazione cliente fallita:', e);
          ElMessage({ message: 'Creazione non riuscita', type: 'error' });
        }
      } finally {
        saving.value = false;
      }
    };

    const euro = (v) =>
      `€ ${Number(v || 0).toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

    return {
      isCompany, types, customer, cars, saving,
      geoState, geoLabel, geoAlternatives, geocode,
      addCarRow, removeCarRow, firstError, submit, euro, partners,
    };
  },
};
</script>

<style scoped>
.acs { padding: 4px 0 90px; }

/* ---- intestazione ---- */
.acs__head { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.acs__back { font-size: 22px; padding: 0 6px; }
.acs__title { margin: 0; font-size: 22px; color: var(--text-primary); }
.acs__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }

/* ---- card ---- */
.acs__card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  overflow: hidden;
  margin-bottom: 16px;
}
.acs__cardHead {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 16px;
  border-bottom: 1px solid var(--border-color);
}
.acs__cardHead h3 { margin: 0; font-size: 15px; color: var(--text-primary); }
.acs__step {
  width: 24px;
  height: 24px;
  flex: none;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--brand-violet);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}
.acs__count {
  margin-left: auto;
  font-size: 12px;
  color: var(--text-tertiary);
  font-variant-numeric: tabular-nums;
}
.acs__cardBody { padding: 16px; }
.acs__note { margin: 0 0 14px; font-size: 12.5px; color: var(--text-tertiary); }

/* ---- scelta del tipo ---- */
.acs__types { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 620px) { .acs__types { grid-template-columns: 1fr; } }
.acs__type {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1.5px solid var(--border-color);
  border-radius: var(--radius-lg);
  background: var(--bg-primary);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color var(--transition-fast);
}
.acs__type:hover { border-color: var(--brand-violet); }
.acs__type.is-active {
  border-color: var(--brand-violet);
  background: var(--bg-secondary);
  box-shadow: inset 0 0 0 1px var(--brand-violet);
}
.acs__typeIcon {
  width: 40px;
  height: 40px;
  flex: none;
  border-radius: var(--radius-md);
  display: grid;
  place-items: center;
  background: var(--bg-tertiary);
  font-size: 19px;
}
.acs__typeName { display: block; font-weight: 700; color: var(--text-primary); }
.acs__typeHint { display: block; font-size: 12px; color: var(--text-tertiary); }

/* ---- campi ---- */
.acs__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 13px;
}
@media (max-width: 860px) { .acs__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 560px) { .acs__grid { grid-template-columns: 1fr; } }
.acs__valetBody { display: flex; flex-direction: column; gap: 12px; margin-top: 12px; }
.acs__field--full { grid-column: 1 / -1; }
.acs__field--wide { grid-column: span 2; }
@media (max-width: 560px) { .acs__field--wide { grid-column: 1 / -1; } }
.acs__field label {
  display: block;
  margin-bottom: 4px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--text-secondary);
}
.acs__req { color: var(--state-danger); }
.acs__field--calc { display: flex; flex-direction: column; justify-content: flex-start; }
.acs__calc {
  display: inline-block;
  padding: 7px 0;
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
}

/* ---- esito geolocalizzazione ---- */
.acs__geo {
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  font-size: 12.5px;
  border: 1px solid transparent;
}
.acs__geo--wait {
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border-color: var(--border-color);
}
.acs__geo--ok {
  color: var(--state-ok);
  background: var(--state-ok-bg);
  border-color: var(--state-ok-line);
}
.acs__geo--fail {
  color: var(--state-attn);
  background: var(--state-attn-bg);
  border-color: var(--state-attn-line);
}
.acs__geoAlt { display: block; margin-top: 3px; color: var(--text-secondary); font-size: 12px; }

/* ---- auto ---- */
.acs__car {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  background: var(--bg-secondary);
  padding: 14px;
}
.acs__car + .acs__car { margin-top: 12px; }
.acs__carHead { display: flex; align-items: center; margin-bottom: 12px; }
.acs__carTitle {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.acs__spacer { flex: 1; }
.acs__remove { color: var(--state-danger); }

/* ---- valet dentro l'auto ---- */
.acs__valet {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed var(--border-color);
}
.acs__valetHead { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.acs__valetTitle { font-weight: 600; font-size: 13px; color: var(--text-primary); }
.acs__valetHint {
  grid-column: 1 / -1;
  margin: 0;
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ---- barra azioni fissa ---- */
.acs__bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  background: var(--bg-primary);
  border-top: 1px solid var(--border-color);
  box-shadow: 0 -4px 16px rgba(24, 32, 64, 0.06);
  padding: 12px 22px;
}
.acs__barInner {
  max-width: 1180px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
}
.acs__error { font-size: 12.5px; color: var(--state-attn); font-weight: 600; }
</style>
